import { Temporal } from '@js-temporal/polyfill';

export const DEFAULT_TIME_ZONE = 'UTC';
export const DEFAULT_EVENT_TIME_ZONE = 'Europe/Moscow';

const pad = (value, length = 2) => String(value).padStart(length, '0');

const normalizeOffset = (value) => (
  value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
);

const normalizeInstantString = (value) => {
  let normalized = String(value).trim();
  normalized = normalized.replace(
    /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?)\s*(Z|[+-]\d{2}:?\d{2})$/i,
    '$1T$2$3'
  );
  normalized = normalized.replace(
    /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?)$/,
    '$1T$2'
  );

  return normalizeOffset(normalized);
};

const hasExplicitOffset = (value) => (
  /(?:Z|[+-]\d{2}:?\d{2})$/i.test(String(value).trim())
);

const normalizePlainDateTimeString = (value) => (
  String(value).trim().replace(
    /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?)$/,
    '$1T$2'
  )
);

const normalizePlainDateString = (value) => {
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value).trim();
};

const coerceInstant = (value, options = {}) => {
  const unit = options.unit || 'milliseconds';
  const timeZone = options.timeZone || DEFAULT_TIME_ZONE;

  if (value == null || value === '') return null;

  try {
    if (value instanceof Temporal.Instant) return value;

    if (typeof value === 'number') {
      const milliseconds = unit === 'seconds' ? value * 1000 : value;
      return Temporal.Instant.fromEpochMilliseconds(Math.round(milliseconds));
    }

    const normalized = normalizeInstantString(value);

    if (hasExplicitOffset(normalized)) {
      return Temporal.Instant.from(normalized);
    }

    return Temporal.PlainDateTime
      .from(normalizePlainDateTimeString(normalized))
      .toZonedDateTime(timeZone)
      .toInstant();
  } catch (_) {
    return null;
  }
};

export const parseInstant = (value, timeZone = DEFAULT_TIME_ZONE) => (
  coerceInstant(value, { timeZone })
);

export const parseEpochInstant = (value, unit = 'milliseconds') => (
  coerceInstant(value, { unit })
);

export const nowInstant = () => (
  Temporal.Instant.fromEpochMilliseconds(Temporal.Now.instant().epochMilliseconds)
);

export const currentYear = (timeZone = DEFAULT_EVENT_TIME_ZONE) => {
  try {
    return Temporal.Now.plainDateISO(timeZone).year;
  } catch (_) {
    return Temporal.Now.plainDateISO(DEFAULT_EVENT_TIME_ZONE).year;
  }
};

export const instantToIso = (value, timeZone = DEFAULT_TIME_ZONE) => {
  const instant = parseInstant(value, timeZone);
  return instant ? instant.toString({ smallestUnit: 'millisecond' }) : null;
};

export const instantToEpochMilliseconds = (value, timeZone = DEFAULT_TIME_ZONE) => {
  const instant = parseInstant(value, timeZone);
  return instant ? instant.epochMilliseconds : null;
};

export const instantDiffMilliseconds = (a, b, timeZone = DEFAULT_TIME_ZONE) => {
  const aMs = instantToEpochMilliseconds(a, timeZone);
  const bMs = instantToEpochMilliseconds(b, timeZone);

  return aMs == null || bMs == null ? null : aMs - bMs;
};

export const addMilliseconds = (value, milliseconds, timeZone = DEFAULT_TIME_ZONE) => {
  const instant = parseInstant(value, timeZone);
  if (!instant || !Number.isFinite(milliseconds)) return null;

  return Temporal.Instant.fromEpochMilliseconds(Math.round(instant.epochMilliseconds + milliseconds));
};

export const subtractMinutes = (value, minutes, timeZone = DEFAULT_TIME_ZONE) => (
  addMilliseconds(value, -minutes * 60 * 1000, timeZone)
);

export const instantToZonedDateTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  const instant = parseInstant(value, timeZone);
  if (!instant) return null;

  try {
    return instant.toZonedDateTimeISO(timeZone);
  } catch (_) {
    return null;
  }
};

export const formatZonedDateTime = (zonedDateTime, format = 'HH:mm:ss.SS') => {
  if (!zonedDateTime) return null;

  const milliseconds = pad(zonedDateTime.millisecond, 3);
  const tokens = {
    YYYY: pad(zonedDateTime.year, 4),
    MM: pad(zonedDateTime.month),
    DD: pad(zonedDateTime.day),
    HH: pad(zonedDateTime.hour),
    mm: pad(zonedDateTime.minute),
    ss: pad(zonedDateTime.second),
    SSS: milliseconds,
    SS: milliseconds.slice(0, 2),
    Z: zonedDateTime.offset
  };

  return Object.keys(tokens)
    .sort((a, b) => b.length - a.length)
    .reduce((result, token) => result.replace(new RegExp(token, 'g'), tokens[token]), format);
};

export const parsePlainDate = (value) => {
  if (!value) return null;

  try {
    return Temporal.PlainDate.from(normalizePlainDateString(value));
  } catch (_) {
    return null;
  }
};

export const formatPlainDate = (value, format = 'DD.MM.YYYY') => {
  const plainDate = parsePlainDate(value);
  if (!plainDate) return null;

  const tokens = {
    YYYY: pad(plainDate.year, 4),
    MM: pad(plainDate.month),
    DD: pad(plainDate.day)
  };

  return Object.keys(tokens)
    .sort((a, b) => b.length - a.length)
    .reduce((result, token) => result.replace(new RegExp(token, 'g'), tokens[token]), format);
};

export const formatInstant = (value, timeZone = DEFAULT_TIME_ZONE, format = 'HH:mm:ss.SS') => (
  formatZonedDateTime(instantToZonedDateTime(value, timeZone), format)
);

export const formatTimezoneOffset = (value, timeZone = DEFAULT_TIME_ZONE) => (
  formatInstant(value, timeZone, 'Z')
);

export const instantToDatetimeLocal = (value, timeZone = DEFAULT_EVENT_TIME_ZONE) => (
  formatInstant(value, timeZone, 'YYYY-MM-DDTHH:mm')
);

export const datetimeLocalToIso = (value, timeZone = DEFAULT_EVENT_TIME_ZONE) => {
  if (!value) return null;

  try {
    return Temporal.PlainDateTime
      .from(normalizePlainDateTimeString(value))
      .toZonedDateTime(timeZone)
      .toInstant()
      .toString({ smallestUnit: 'millisecond' });
  } catch (_) {
    return null;
  }
};

export const formatRawPassingDatetime = (value, timeZone = DEFAULT_EVENT_TIME_ZONE) => (
  formatInstant(value, timeZone, 'YYYY-MM-DD HH:mm:ss.SSS Z')
);

export const formatRawPassingInputDatetime = (value, timeZone = DEFAULT_EVENT_TIME_ZONE) => (
  formatInstant(value, timeZone, 'YYYY-MM-DD HH:mm:ss.SSS')
);

export const formatDurationMs = (milliseconds, options = {}) => {
  if (milliseconds == null || Number.isNaN(Number(milliseconds))) return null;

  const fractionDigits = options.fractionDigits == null ? 2 : options.fractionDigits;
  let totalMilliseconds = Math.round(Number(milliseconds));
  const sign = totalMilliseconds < 0 ? '-' : '';
  totalMilliseconds = Math.abs(totalMilliseconds);

  const hours = Math.floor(totalMilliseconds / 3600000);
  const minutes = Math.floor((totalMilliseconds % 3600000) / 60000);
  const seconds = Math.floor((totalMilliseconds % 60000) / 1000);
  const millisecondsPart = totalMilliseconds % 1000;
  const base = `${sign}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  if (fractionDigits <= 0) return base;

  return `${base}.${pad(millisecondsPart, 3).slice(0, fractionDigits)}`;
};

export const formatDurationSeconds = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return null;

  return formatDurationMs(Number(seconds) * 1000, { fractionDigits: 0 });
};

export const formatMinuteSecondDuration = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return null;

  let totalSeconds = Math.round(Number(minutes) * 60);
  const sign = totalSeconds < 0 ? '-' : '';
  totalSeconds = Math.abs(totalSeconds);

  const minutesPart = Math.floor(totalSeconds / 60);
  const secondsPart = totalSeconds % 60;

  return `${sign}${pad(minutesPart)}:${pad(secondsPart)}`;
};
